import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SwellingSaveModel } from '../models/swelling';
import { Router } from '@angular/router';
import { APIResponse } from '../../models/APIResponse';
import { environment } from '../../../environments/environment';
import { StatusParams, SwellingPartialSave, Vw_SwellReportData } from '../models/swelling';
import { BehaviorSubject } from 'rxjs';
import { Chart, Plugin, ChartDataset } from 'chart.js';
import { toFont } from 'chart.js/helpers';
import { NUMBERS_CONSTANTS } from '../../common/numbers-constants';
import { apiV1 } from '../../common/texts-constants';
import { AdditionActualGasInputStr, AdditionPressureVolumeInputStr, AdditionTargetGasInputStr, SwellingCompositionComponentModel, SwellingDefaultCompositionData, SwellingEmthermMolPercentInput, SwellingGasAdditionStageModel, SwellingMolCompInput, SwellingMolCompInputStr, SwellingOrgResFluidCompInput, SwellingOrgResFluidCompInputStr, SwellingOriginalFluidComponentModel, SwellingSaveInput, SwellingValidationResult } from '../models/swelling';
import { Components, ComponentMasters } from '../../models/shared';
import { FormGroup, Validators } from '@angular/forms';
import { SwellingValidatorService } from './swelling-validator.service';
import { SWELLING_MESSAGES } from '../../common/messages-constants';
import { COMPONENT_CONSTANTS } from '../../common/constants';

@Injectable({
  providedIn: 'root'
})
export class SwellingService {
  private http = inject(HttpClient);
  private validatorService = inject(SwellingValidatorService);
  router = inject(Router);

  private apiBaseUrl = environment.apiUrl+apiV1;
  public statusChange$ = new BehaviorSubject<StatusParams>({ statusId: 0, statusName: '' });

  private _palette: string[] = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'];
  private _colorByTemp: Map<number, string> = new Map<number, string>();

  UpdateStatus(status: StatusParams) {
    this.statusChange$.next(status);
  }

  /**
   * Builds the default composition/emtherm data derived from component masters.
   * Mol composition (step 3) only includes components up to and including C12.
   * @param componentMasters - Component masters returned by SharedService.GetComponentMasters()
   */
  PrepareDefaultCompositionData(componentMasters: ComponentMasters): SwellingDefaultCompositionData {
    const molCompInputData: SwellingMolCompInputStr[] = [];
    const orgResFluidCompInput: SwellingOrgResFluidCompInputStr[] = [];
    const emthermMolPercentInput: SwellingEmthermMolPercentInput[] = [];
    const components = componentMasters.components;
    const c12Index = components.findIndex((comp: Components) => comp.component.toLowerCase() === COMPONENT_CONSTANTS.C12.toLowerCase());

    for (let i = 0; components.length > i; i++) {
      if (i <= c12Index && c12Index !== -1) {
        molCompInputData.push({
          molCompId: NUMBERS_CONSTANTS.zero,
          molComponents: components[i].component,
          targetMolePercent: null,
          measuredMolePercent: null,
          errorPercent: null
        });
      }

      orgResFluidCompInput.push({
        compId: NUMBERS_CONSTANTS.zero,
        component: components[i].component,
        componentName: components[i].componentName,
        molePercent: null
      });
    }

    for (let i = 0; componentMasters.emthermComponents.length > i; i++) {
      emthermMolPercentInput.push({
        emthermMolPercentInputId: NUMBERS_CONSTANTS.zero,
        molComponents: componentMasters.emthermComponents[i].component,
        averageMolePercent: NUMBERS_CONSTANTS.zero
      });
    }

    return { molCompInputData, orgResFluidCompInput, emthermMolPercentInput };
  }

  /**
   * Sets required validators on form controls
   * @param form - FormGroup to update
   * @param controlNames - Array of control names to set as required
   */
  SetFormControlsAsRequired(form: FormGroup, controlNames: string[]) {
    controlNames.forEach((controlName) => {
      const control = form.get(controlName);
      if (control) {
        control.setValidators([Validators.required]);
        control.updateValueAndValidity();
      }
    });
    form.markAllAsTouched();
  }

  /**
   * Validates Step 1 (Initial Properties) and returns validation result with error and warning messages
   * @param form - The initialForm FormGroup
   * @param targetGasArray - The addition target gas array
   * @param tempCount - Number of temperature additions
   * @param validateDataRange - Whether to validate data ranges
   */
  ValidateStep1WithMessages(
    form: FormGroup,
    targetGasArray: Record<string, unknown>[],
    tempCount: number,
    validateDataRange: boolean
  ): SwellingValidationResult {
    form.markAllAsTouched();
    const requiredControls = this.validatorService.GetStep1RequiredControls(tempCount);
    this.SetFormControlsAsRequired(form, requiredControls);

    const errors: string[] = [];
    const warnings: string[] = [];

    // Check required fields
    if (form.invalid) {
      errors.push(SWELLING_MESSAGES.enterDataAllRequiredStep1);
    }

    // Check data ranges (warnings only)
    if (validateDataRange) {
      const formValues = form.getRawValue();
      const rangeWarnings = this.validatorService.ValidateStep1Ranges(formValues);
      warnings.push(...rangeWarnings);

      const targetGasWarnings = this.validatorService.ValidateTargetGasRanges(
        targetGasArray as unknown as AdditionTargetGasInputStr[]
      );
      warnings.push(...targetGasWarnings);
    }

    if (form.invalid) {
      errors.push(SWELLING_MESSAGES.fillAllRequiredFieldsStep1);
    }

    return {
      status: errors.length === NUMBERS_CONSTANTS.zero,
      errors,
      warnings
    };
  }

  /**
   * Validates Step 2 (Composition Components) - range only, not required
   */
  ValidateStep2WithMessages(compMolePercentArray: Record<string, unknown>[], validateDataRange: boolean): SwellingValidationResult {
    const warnings: string[] = [];

    if (validateDataRange) {
      const rangeWarnings = this.validatorService.ValidateStep2Ranges(
        compMolePercentArray as unknown as { targetMolePercent: string | number | null; measuredMolePercent: string | number | null; }[]
      );
      warnings.push(...rangeWarnings);
    }

    return {
      status: true,
      errors: [],
      warnings
    };
  }

  /**
   * Validates Step 3 (Original Reservoir Fluid) - range only, not required
   */
  ValidateStep3WithMessages(orgResFluidCompArray: Record<string, unknown>[], validateDataRange: boolean): SwellingValidationResult {
    const warnings: string[] = [];

    if (validateDataRange) {
      const rangeWarnings = this.validatorService.ValidateStep3Ranges(
        orgResFluidCompArray as unknown as { molePercent: string | number | null; }[]
      );
      warnings.push(...rangeWarnings);
    }

    return {
      status: true,
      errors: [],
      warnings
    };
  }

  /**
   * Validates Step 4 (Actual Gas + Multi-Temperature PVT) - range only, not required
   */
  ValidateStep4WithMessages(
    actualGasArray: Record<string, unknown>[],
    temperatureArray: Record<string, unknown>[],
    validateDataRange: boolean
  ): SwellingValidationResult {
    const warnings: string[] = [];

    if (validateDataRange) {
      const rangeWarnings = this.validatorService.ValidateStep4Ranges(
        actualGasArray as unknown as { actualGasAdded: string | number | null; }[],
        temperatureArray as unknown as { additionTemperature: string | number | null; additions: AdditionPressureVolumeInputStr[] }[]
      );
      warnings.push(...rangeWarnings);
    }

    return {
      status: true,
      errors: [],
      warnings
    };
  }

  //Api call to Partial Save Swelling details
  PartialSaveSwelling(swellingDetails: SwellingPartialSave) {
    return this.http.post<APIResponse<SwellingSaveModel>>(this.apiBaseUrl + 'swelling/PartialSaveSwelling', swellingDetails);
  }

  GetSwelling(labTestRepeatId: number) {
      return this.http.get<APIResponse<SwellingSaveModel>>(this.apiBaseUrl + `Swelling/GetSwelling?testRepeatId=${labTestRepeatId}`);
  }

  SaveSwelling(data: SwellingSaveModel) {
      return this.http.post<APIResponse<SwellingSaveModel>>(this.apiBaseUrl + `Swelling/SaveSwelling`, data);
  }

  UpdateSwelling(data: SwellingSaveModel) {
      return this.http.post<APIResponse<SwellingSaveModel>>(this.apiBaseUrl + `Swelling/UpdateSwelling`, data);
  }

  PartialSwelling(data: SwellingSaveModel) {
      return this.http.post<APIResponse<SwellingSaveModel>>(this.apiBaseUrl + `Swelling/PartialSaveSwelling`, data);
  }

  GetFinalReportSwelling(testRepeatId: number) {
      return this.http.get<APIResponse<SwellingSaveModel>>(this.apiBaseUrl + 'Swelling/GetFinalReportSwelling?testRepeatId=' + testRepeatId);
  }

  BuildTargetGasOnlyModel(targetGasData: AdditionTargetGasInputStr[]): SwellingGasAdditionStageModel[] {
    return targetGasData.map((row) => ({
      gasAdditionStageId: row.additionTargetGasInputId || NUMBERS_CONSTANTS.zero,
      targetInjGasMolPct: this.ToNumberOrNull(row.targetInjGasMol),
      addition: row.addition ?? '',
      totalMoles: null,
      injGasMoles: null,
      stageAddedGasMoles: null,
      stageAddedGasGrams: this.ToNumberOrNull(row.targetStageAddedGas),
      actualGasAddedGrams: null,
      cumGasAddedGrams: null,
      cumGasAddedMoles: null,
      injGas: null,
      massInCell: null,
      satPDensity: null,
      relativeVolume: null,
      calcMW: null,
      cumGasAddedCc: null,
      cumGasAddedScf: null,
      relativeGOR: null,
      temperatureNumber: NUMBERS_CONSTANTS.zero,
      temperature: null,
      satP: null,
      satPVolOfCell: null,
      systemType: null
    }));
  }

  BuildCompositionComponentModel(
    data: SwellingMolCompInput[],
    components: Components[]
  ): SwellingCompositionComponentModel[] {
    return data.map((row) => {
      const component = components.find(c => c.component === row.molComponents);
      return {
        compositionComponentId: row.molCompId || NUMBERS_CONSTANTS.zero,
        componentId: component?.componentId ?? NUMBERS_CONSTANTS.zero,
        component: row.molComponents ?? '',
        mw: undefined,
        targetMolPct: this.ToNumberOrNull(row.targetMolePercent) ?? undefined,
        measuredMolPct: this.ToNumberOrNull(row.measuredMolePercent) ?? undefined,
        errorPct: NUMBERS_CONSTANTS.zero,
        mwCalc: undefined,
        measuredWtPctCalc: undefined,
        measuredWtPct: undefined
      };
    });
  }

  BuildPropertiesModel(formData: Record<string, unknown>): Record<string, number | null> {
    return {
      propertyId: this.ToNumberOrNull(formData['propertyDataId']) || NUMBERS_CONSTANTS.zero,
      reservoirTemperature: this.ToNumberOrNull(formData['reservoirTemperature']),
      reservoirPressure: this.ToNumberOrNull(formData['reservoirPressure']),
      originalVolumePVTCell: this.ToNumberOrNull(formData['originalVolumePVTCell']),
      originalVolumePVTCellPressure: this.ToNumberOrNull(formData['originalVolumePVTCellPressure']),
      densityOfFluid: this.ToNumberOrNull(formData['densityOfFluid']),
      densityOfFluidPressure: this.ToNumberOrNull(formData['densityOfFluidPressure']),
      initialMass: this.ToNumberOrNull(formData['initialMass']),
      mwOfFluid: this.ToNumberOrNull(formData['molecularWeight']),
      molesOriginalResFluid: this.ToNumberOrNull(formData['molesOriginalResFluid']),
      fluidDensitySatPResT: this.ToNumberOrNull(formData['fluidDensitySatPResT']),
      fluidDensitySatPT2: this.ToNumberOrNull(formData['fluidDensitySatPT2']),
      fluidDensitySatPT3: this.ToNumberOrNull(formData['fluidDensitySatPT3']),
      fluidDensitySatPT4: this.ToNumberOrNull(formData['fluidDensitySatPT4']),
      fluidDensitySatPT5: this.ToNumberOrNull(formData['fluidDensitySatPT5']),
      originalVolumePVTCellSatP: null,
      injectionGasMW: null,
      injectionGasPropDensity: this.ToNumberOrNull(formData['injectionGasPropDensity']),
      oilVolumeAtSatP: null,
      sumMolPct: null,
      sumWtPct: null,
      sumAdd1MolPct: null,
      sumAdd2MolPct: null,
      sumAdd3MolPct: null,
      sumAdd4MolPct: null,
      sumAdd5MolPct: null,
      sumAdd6MolPct: null,
      sumAdd7MolPct: null,
      sumMwcalc1: null,
      sumMwcalc2: null,
      sumMwcalc3: null,
      sumMwcalc4: null,
      sumMwcalc5: null,
      sumMwcalc6: null,
      sumMwcalc7: null
    };
  }

  BuildOriginalFluidComponentModel(
    data: SwellingOrgResFluidCompInput[],
    components: Components[]
  ): SwellingOriginalFluidComponentModel[] {
    return data.map((row) => {
      const component = components.find(c => c.component === row.component);
      return {
        originalFluidComponentId: row.compId || NUMBERS_CONSTANTS.zero,
        componentId: component?.componentId ?? NUMBERS_CONSTANTS.zero,
        component: row.component ?? '',
        componentName: row.componentName ?? '',
        mw: undefined,
        molPct: this.ToNumberOrNull(row.molePercent) ?? undefined,
        wtPct: undefined,
        add1: undefined, add2: undefined, add3: undefined, add4: undefined, add5: undefined, add6: undefined, add7: undefined,
        add1MolPct: undefined, add2MolPct: undefined, add3MolPct: undefined, add4MolPct: undefined,
        add5MolPct: undefined, add6MolPct: undefined, add7MolPct: undefined,
        mwCalc1: undefined, mwCalc2: undefined, mwCalc3: undefined, mwCalc4: undefined,
        mwCalc5: undefined, mwCalc6: undefined, mwCalc7: undefined
      };
    });
  }

  BuildGasAdditionStageModel(
    targetGasData: AdditionTargetGasInputStr[],
    actualGasData: AdditionActualGasInputStr[],
    tempData: {
      additionTemperature: number | string | null;
      additions: AdditionPressureVolumeInputStr[];
    }[]
  ): SwellingGasAdditionStageModel[] {
    const result: SwellingGasAdditionStageModel[] = [];

    tempData.forEach((tempBlock, tempIndex: number) => {
      const tempNumber = tempIndex + NUMBERS_CONSTANTS.one;
      const tempValue = tempBlock.additionTemperature;
      const pvtAdditions = tempBlock.additions;

      pvtAdditions.forEach((pvtRow: AdditionPressureVolumeInputStr, addIndex: number) => {
        const targetRow = targetGasData[addIndex];
        const actualRow = actualGasData[addIndex];

        result.push({
          gasAdditionStageId: pvtRow.gasAdditionStageId || NUMBERS_CONSTANTS.zero,
          targetInjGasMolPct: this.ToNumberOrNull(targetRow?.targetInjGasMol),
          totalMoles: null,
          injGasMoles: null,
          stageAddedGasMoles: null,
          stageAddedGasGrams: this.ToNumberOrNull(targetRow?.targetStageAddedGas),
          actualGasAddedGrams: this.ToNumberOrNull(actualRow?.actualGasAdded),
          cumGasAddedGrams: null,
          cumGasAddedMoles: null,
          injGas: null,
          massInCell: null,
          satPDensity: null,
          relativeVolume: null,
          calcMW: null,
          cumGasAddedCc: null,
          cumGasAddedScf: null,
          relativeGOR: null,
          addition: pvtRow.addition ?? '',
          temperatureNumber: tempNumber,
          temperature: this.ToNumberOrNull(tempValue),
          satP: this.ToNumberOrNull(pvtRow.saturationPressure),
          satPVolOfCell: this.ToNumberOrNull(pvtRow.volumeOfCell),
          systemType: pvtRow.systemType ?? null
        });
      });
    });

    return result;
  }

  HasStep1Data(formData: Record<string, unknown>, targetGasData: AdditionTargetGasInputStr[]): boolean {
    const userFields = [
      'originalVolumePVTCell', 'originalVolumePVTCellPressure',
      'densityOfFluid', 'densityOfFluidPressure',
      'molecularWeight', 'fluidDensitySatPResT', 'fluidDensitySatPT2',
      'injectionGasPropDensity'
    ];
    if (userFields.some(field => this.HasValue(formData[field]))) {
      return true;
    }

    return targetGasData.some((row, index) =>
      this.HasValue(row.targetInjGasMol) ||
      (this.HasValue(row.targetStageAddedGas) && !(index === NUMBERS_CONSTANTS.zero && Number(row.targetStageAddedGas) === NUMBERS_CONSTANTS.zero))
    );
  }

  HasStep4Data(
    actualGasData: AdditionActualGasInputStr[],
    temperatureData: { additionTemperature: number | null; additions: AdditionPressureVolumeInputStr[]; }[]
  ): boolean {
    const hasActualGas = actualGasData.some(row =>
      (this.HasValue(row.actualGasAdded) && row.actualGasAdded !== NUMBERS_CONSTANTS.zero) ||
      this.HasValue(row.resultingFluidMolWt) ||
      this.HasValue(row.massInCell)
    );
    if (hasActualGas) {
      return true;
    }

    return temperatureData.some(block =>
      this.HasValue(block.additionTemperature) ||
      block.additions?.some(addition =>
        this.HasValue(addition.saturationPressure) || this.HasValue(addition.volumeOfCell) || this.HasValue(addition.systemType)
      )
    );
  }

  HasCompositionData(data: SwellingMolCompInput[]): boolean {
    return data.some(row =>
      (row.targetMolePercent !== null && row.targetMolePercent !== undefined && row.targetMolePercent !== NUMBERS_CONSTANTS.zero) ||
      (row.measuredMolePercent !== null && row.measuredMolePercent !== undefined && row.measuredMolePercent !== NUMBERS_CONSTANTS.zero)
    );
  }

  HasOriginalFluidData(data: SwellingOrgResFluidCompInput[]): boolean {
    return data.some(row =>
      row.molePercent !== null && row.molePercent !== undefined && row.molePercent !== NUMBERS_CONSTANTS.zero
    );
  }

  BuildSwellingSaveModel(input: SwellingSaveInput): SwellingSaveModel {
    return {
      swellingPropertiesModel: this.BuildPropertiesModel(input.propertiesData),
      swellingCompositionComponentModel: this.BuildCompositionComponentModel(input.compositionData, input.components),
      swellingOriginalFluidComponentModel: this.BuildOriginalFluidComponentModel(input.originalFluidData, input.components),
      swellingGasAdditionStageModel: this.BuildGasAdditionStageModel(input.targetGasData, input.actualGasData, input.temperatureData),
      swellReport: [],
      swellInjectionGasComposition: [],
      swellCalculatedCompositionalBalance: [],
      userId: input.userId,
      role: input.role,
      labRepeatId: input.labRepeatId,
      comments: input.comments
    };
  }

  private HasValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private ToNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '' || value === '--') {
      return null;
    }
    const num = Number(value);
    return isNaN(num) ? null : num;
  }

  CreateSwellingChartReport(swellingData: Vw_SwellReportData[], ctxPO1: CanvasRenderingContext2D) {
    const groupedData = new Map<number, { x: number; y: number }[]>();
    const tempLabelMap = new Map<number, number>();
 
    for (const d of swellingData) {
      if (d.temperature !== null && d.temperature !== undefined && d.moleFracInjGas !== null && d.moleFracInjGas !== undefined && d.saturationPressure !== null && d.saturationPressure !== undefined) {
        if (!groupedData.has(d.temperatureNumber)) {
          groupedData.set(d.temperatureNumber, []);
          tempLabelMap.set(d.temperatureNumber, d.temperature);
        }
        groupedData.get(d.temperatureNumber)!.push({
          x: d.moleFracInjGas,
          y: d.saturationPressure
        });
      }
    }

    // Sort by temperatureNumber
    const sortedEntries = Array.from(groupedData.entries()).sort((a, b) => a[0] - b[0]);

    // Create datasets dynamically
    const datasets = sortedEntries.map(([tempNumber, points]) => {
      const temp = tempLabelMap.get(tempNumber) ?? tempNumber;
      return {
        label: `${temp} F`,
        data: points,      
        borderColor: "#000",
        backgroundColor: this.GetColorForTemp(tempNumber),
        pointBorderColor: '#000',
        pointBackgroundColor: this.GetColorForTemp(tempNumber),
        pointRadius: 16,
        pointBorderWidth: 3,
        showLine: true,
        fill: false,
        borderWidth: NUMBERS_CONSTANTS.four,
        pointStyle: 'circle',
        tension : 0
      };
    });
    //For Pressure V/Vsat chart
    const swellingChart = new Chart(ctxPO1, {
      type: 'scatter',
      data: {
        datasets
      },
      options: {
        animation: false,       
        plugins: {
          legend: {
            display: false,
            position: 'right',
            align: 'center',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 14,
              padding: 12,
              color: "#000",
              font: { size: 20, weight: 'bold' },
              filter: () => true
            }
           },
          datalabels: { 
            display: false,
          },
        },
        scales: {
          x: {
            border: {
              display: true,
              color: 'black',
              width: NUMBERS_CONSTANTS.three
            },
            title: {
              display: true,
              text: 'Mole Frac Injection Gas',
              font: {
                size: 35,
                weight: 'bold'
              },
              color: 'back'
            },
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
                size: 35
              },
              color: 'black',
              stepSize: 0.2
            },
            min: 0.0,
            max: 1.0,
            grid: {
              display: false,
            },

          },
          y: {
            title: {
              display: true,
              text: 'Saturation Pressure (psia)',
              font: {
                size: 35,
                weight: 'bold'
              },
              color: 'black'
            },
            border: {
              display: true,
              color: 'black',
              width: NUMBERS_CONSTANTS.three
            },
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: 'black',
              font: {
                weight: 'bold',
                size: 35
              },
              stepSize: 2000,
              padding: 30,
              callback: (value) => value.toString()
            },
            min: 0,
            max: 16000,
            
            grid: {
              display: false,
            },
          },
        }
      },
      plugins: [this.boxBorderPlugin, this.axisOriginTicksPlugin, this.pointsAboveAxesPlugin, this.inPlotLegend],
    });
    return swellingChart;
  }
  
   GetColorForTemp(temp: string | number) {
    // Initialize palette and cache once on the instance
    if (!this._palette) {
      this._palette = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'];
    }
    if (!this._colorByTemp) {
      this._colorByTemp = new Map<number, string>();
    }
 
    // Normalize temperature to a number key
    const t = typeof temp === 'number' ? temp : Number.parseInt(String(temp), 10);
    if (Number.isNaN(t)) {
      // Fallback: if temp is not numeric, still return a color deterministically
      const key = String(temp);
      const idx = this._colorByTemp.size % this._palette.length;
      // Store using a negative hash bucket to avoid colliding with numeric keys
      const hashed = -Math.abs(this.hashString(key));
      if (!this._colorByTemp.has(hashed)) {
        this._colorByTemp.set(hashed, this._palette[idx]);
      }
      return this._colorByTemp.get(hashed)!;
    }
 
    // If this temperature doesn’t have a color yet, assign next palette color
    if (!this._colorByTemp.has(t)) {
      const idx = this._colorByTemp.size % this._palette.length;
      this._colorByTemp.set(t, this._palette[idx]);
    }
 
    return this._colorByTemp.get(t)!;
  }
 
  // Small helper to hash strings deterministically (used only if temp isn't numeric)
  hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) | 0;
    }
    return hash;
  }

  
  axisOriginTicksPlugin: Plugin = {
    id: 'axisOriginTicksPlugin',
    beforeDraw: (chart: Chart) => {
      const ctx = chart.ctx;
      const xScale = chart.scales['x'];
      const yScale = chart.scales['y'];
      const tickLength = 10;
      ctx.save();
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 2;
      // Draw ticks on X axis (inside plot area only)
      xScale.ticks.forEach(tick => {
        const x = xScale.getPixelForValue(tick.value);
        const yEnd = chart.chartArea.bottom;

        if (x >= chart.chartArea.left && x <= chart.chartArea.right) {
          ctx.beginPath();
          ctx.moveTo(x, yEnd - tickLength); // bottom edge inward
          ctx.lineTo(x, yEnd);              // bottom edge
          ctx.stroke();
        }
      });

      // Draw ticks on Y axis (inside plot area only)
      yScale.ticks.forEach(tick => {
        const y = yScale.getPixelForValue(tick.value);
        const xStart = chart.chartArea.left;

        if (y >= chart.chartArea.top && y <= chart.chartArea.bottom) {
          ctx.beginPath();
          ctx.moveTo(xStart, y);              // left edge
          ctx.lineTo(xStart + tickLength, y); // left edge inward
          ctx.stroke();
        }
      });

      ctx.restore();

    }
  };

  boxBorderPlugin: Plugin = {
    id: 'boxBorderPlugin',
    beforeDraw: (chart: Chart) => {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;

      ctx.save();
      ctx.strokeStyle = 'black';
      ctx.lineWidth = NUMBERS_CONSTANTS.three;

      // Top border
      ctx.beginPath();
      ctx.moveTo(chartArea.left, chartArea.top);
      ctx.lineTo(chartArea.right, chartArea.top);
      ctx.stroke();

      // Right border
      ctx.lineWidth = NUMBERS_CONSTANTS.four;
      ctx.beginPath();
      ctx.moveTo(chartArea.right, chartArea.top);
      ctx.lineTo(chartArea.right, chartArea.bottom);
      ctx.stroke();

      ctx.restore();
    }
  };
  
    
  legendLineThroughCirclePlugin = {
    id: 'legendLineThroughCircle',
    beforeDraw(chart: Chart) {
      const ctx = chart.ctx;
      const legend = chart.legend;

      if (!legend) return;

      const font = toFont(legend.options.labels.font as Partial<Record<string, unknown>>);
      const fontSize = font.size;

      const baseOffset = 80;
      const lineSpacing = 50; 

      legend?.legendItems?.forEach((item, index) => {
        const boxWidth = legend.options.labels.boxWidth;
        const boxHeight = legend.options.labels.boxHeight || fontSize;
        const x = legend.left + 200; // approximate position
        const y = legend.top + baseOffset + index * lineSpacing;
        const spacing = 4;
        const lineY = y + boxHeight / 6;

        ctx.save();
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x - spacing, lineY);
        ctx.lineTo(x + boxWidth + spacing, lineY);
        ctx.stroke();
        ctx.restore();
      });
    }
  };

  pointsAboveAxesPlugin = {
    id: 'pointsAboveAxes',
    afterDraw(chart: Chart) {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;

      // Expand clipping area by the maximum point radius
      const padding = 20; // Adjust based on your largest pointRadius
      ctx.save();
      ctx.beginPath();
      ctx.rect(
        chartArea.left - padding,
        chartArea.top,
        chartArea.right - chartArea.left + padding * 2,
        chartArea.bottom - chartArea.top + padding
      );
      ctx.clip();

      // Redraw points
      chart.data.datasets.forEach((_, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (!meta.hidden) {
          meta.controller.draw();
        }
      });

      ctx.restore();
    }
  };

 GetSwellingChartData(swellingData: Vw_SwellReportData[]): { groupedData: Record<number, { x: number; y: number }[]>, tempLabelMap: Record<number, number> } {
  const groupedData: Record<number, { x: number; y: number }[]> = {};
  const tempLabelMap: Record<number, number> = {}; 

  swellingData.forEach(d => {
    if (d.temperature !== null && d.temperature !== undefined && d.moleFracInjGas !== null && d.moleFracInjGas !== undefined && d.saturationPressure !== null && d.saturationPressure !== undefined) {
      if (!groupedData[d.temperatureNumber]) {
        groupedData[d.temperatureNumber] = [];
        tempLabelMap[d.temperatureNumber] = d.temperature;
      }
      groupedData[d.temperatureNumber].push({
        x: d.moleFracInjGas,
        y: d.saturationPressure
      });
    }
  });

  return { groupedData, tempLabelMap };
}

CreateChart(
  ctx: CanvasRenderingContext2D,
  datasets: ChartDataset[],
  plotTitle: string,
  xAxisTitle: string,
  yAxisTitle: string,
  xMin = 0,
  xMax = 1.0,
  xStep = 0.2,
  yMin = 0,
  yMax = 16000,
  yStep = 2000
): Chart {
  return new Chart(ctx, {
    type: 'scatter',
    data: {
      datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 20
        }
      },
      plugins: {
        title: {
          display: true,
          text: plotTitle,
          font: { size: 16, weight: 'bold' },
          color: '#000',
          padding: { top: 8, bottom: 12 }
        },
        legend: {
          display: true,
          position: 'right',
          labels: {
            usePointStyle: true,
            font: { size: 12 },
            color: '#000'
          }
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true
            },
            mode: 'xy',
          },
          pan: {
            enabled: true,
            mode: 'xy'
          }
        }
      },
      scales: {
        x: {
          border: {
            display: true,
            color: 'black',
            width: 2
          },
          title: {
            display: true,
            text: xAxisTitle,
            font: {
              size: 14,
              weight: 'bold'
            },
            color: 'black'
          },
          type: 'linear',
          position: 'bottom',
          ticks: {
            font: {
              weight: 'bold',
              size: 12
            },
            color: 'black',
            stepSize: xStep
          },
          min: xMin,
          max: xMax,
          grid: {
            display: false,
            color: '#e0e0e0'
          }
        },
        y: {
          title: {
            display: true,
            text: yAxisTitle,
            font: {
              size: 14,
              weight: 'bold'
            },
            color: 'black'
          },
          border: {
            display: true,
            color: 'black',
            width: 2
          },
          type: 'linear',
          beginAtZero: true,
          ticks: {
            color: 'black',
            font: {
              weight: 'bold',
              size: 12
            },
            stepSize: yStep
          },
          min: yMin,
          max: yMax,
          grid: {
            display: false,
            color: '#e0e0e0'
          }
        }
      },
      elements: {
        point: {
          radius: 6,
          hoverRadius: 8
        }
      },
      clip: 0
    },
    plugins: [this.boxBorderPlugin, this.axisOriginTicksPlugin]
  });
}

  inPlotLegend = {
    id: 'inPlotLegend',
    afterDatasetsDraw(chart: Chart) {
      const { ctx, chartArea, data } = chart;
 
      // layout
      const paddingX = 75;
      const paddingY = 60;
      const lineHeight = 60;
 
      // visuals
      const markerRadius = 6;
      const markerStroke = 2;
      const gapAfterMarker = 20; // space between marker and label
      const miniLineLen = 40;    // centered line length behind the marker
 
      ctx.save();
      ctx.font = '30px sans-serif';
      ctx.fillStyle = '#000';
      ctx.textBaseline = 'middle';
 
      let y = chartArea.top + paddingY;
      const x = chartArea.left + paddingX;
 
      data.datasets.forEach((ds: ChartDataset) => {
        // --- 1) line behind the point (draw first) ---
        const half = miniLineLen / 2;
        ctx.beginPath();
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = (typeof ds.borderColor === 'string' ? ds.borderColor : '#000');
        ctx.moveTo(x - half, y);
        ctx.lineTo(x + half, y);
        ctx.stroke();
 
        // --- 2) point (draw second, so it's on top of the line) ---
        ctx.beginPath();
        ctx.lineWidth = markerStroke;
        ctx.strokeStyle = '#000';
        const bgColor = typeof ds.backgroundColor === 'string' ? ds.backgroundColor : '#666';
        ctx.fillStyle = bgColor;
        ctx.arc(x, y, markerRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
 
        // --- 3) label (to the right) ---
        const label = ds.label ?? '';
        const labelX = x + markerRadius + gapAfterMarker;
        ctx.fillStyle = '#000';
        ctx.fillText(label, labelX, y);
 
        y += lineHeight;
      });
 
      ctx.restore();
    }
  };
}


